<!-- ENG-5981: store owner assistant (shown when the store's store_agent flag is on) -->
<div *ngIf="allowed" class="ai-chat-widget" [class.fullscreen]="isFullscreen">
  <button *ngIf="!isOpen" mat-fab class="chat-fab" matTooltip="Labrador Assistant" (click)="toggleChat()">
    <mat-icon>smart_toy</mat-icon>
  </button>

  <div *ngIf="isOpen" class="chat-panel">
    <div class="chat-header">
      <span class="chat-title">Labrador Assistant</span>
      <div class="header-actions">
        <button mat-icon-button (click)="toggleActivity()" [matTooltip]="showActivity ? 'Back to chat' : 'Activity'">
          <mat-icon>{{ showActivity ? 'chat' : 'history' }}</mat-icon>
        </button>
        <button mat-icon-button (click)="toggleSettings()" [matTooltip]="showSettings ? 'Back to chat' : 'Who can use it'">
          <mat-icon>{{ showSettings ? 'chat' : 'manage_accounts' }}</mat-icon>
        </button>
        <button mat-icon-button (click)="sendToPhone()" matTooltip="Send to my phone"><mat-icon>smartphone</mat-icon></button>
        <button mat-icon-button (click)="reset(true)" matTooltip="New chat"><mat-icon>refresh</mat-icon></button>
        <button mat-icon-button (click)="toggleFullscreen()" [matTooltip]="isFullscreen ? 'Minimize' : 'Maximize'">
          <mat-icon>{{ isFullscreen ? 'close_fullscreen' : 'open_in_full' }}</mat-icon>
        </button>
        <button mat-icon-button (click)="toggleChat()"><mat-icon>close</mat-icon></button>
      </div>
    </div>

    <div *ngIf="needsPin" class="chat-messages pin-gate">
      <div class="pin-title">Enter a Master Admin PIN</div>
      <input type="password" inputmode="numeric" class="chat-input pin-input" maxlength="10" placeholder="PIN"
             [(ngModel)]="pinInput" (keydown.enter)="submitPin()" />
      <div *ngIf="pinError" class="pin-error">{{ pinError }}</div>
      <button mat-flat-button color="primary" [disabled]="!pinInput" (click)="submitPin()">Open assistant</button>
    </div>

    <div *ngIf="showSettings && !needsPin" class="chat-messages settings">
      <div *ngIf="settingsLoading" class="typing-indicator"><span class="dot"></span><span class="dot"></span><span class="dot"></span></div>
      <div *ngIf="settingsError" class="activity-error">{{ settingsError }}</div>
      <ng-container *ngIf="!settingsLoading && !settingsError">
        <div class="set-section">
          <div class="set-title">Managers who can use the assistant</div>
          <div class="set-help">Only people with "Labrador Assistant" turned on in Manage Users &gt; Permissions can open it.
            Master Admins with it can change everything; give a manager only the areas they need.</div>
          <div *ngFor="let g of grants" class="set-row">
            <div class="set-name">{{ g.name }}
              <span *ngIf="noPermission(g.user_id)" class="set-muted set-warn">· permission off, cannot open it yet</span></div>
            <div class="set-areas">
              <label *ngFor="let a of areaKeys" class="set-chip" [class.on]="g.domains.includes(a)">
                <input type="checkbox" [checked]="g.domains.includes(a)" (change)="toggleGrantArea(g, a)" /> {{ areaLabels[a] || a }}
              </label>
            </div>
            <button class="set-link danger" (click)="removeGrant(g)">Remove</button>
          </div>
          <div *ngIf="!grants.length" class="set-help">No managers yet.</div>
          <div class="set-add">
            <select [(ngModel)]="newGrantUser" class="set-select">
              <option [ngValue]="null">Add a manager…</option>
              <option *ngFor="let p of staffWithoutAccess()" [ngValue]="p.user_id">{{ p.name }}{{ p.role ? ' · ' + p.role : '' }}{{ p.has_permission === false ? ' · permission off' : '' }}</option>
            </select>
            <div *ngIf="newGrantUser" class="set-areas">
              <label *ngFor="let a of areaKeys" class="set-chip" [class.on]="newGrantAreas[a]">
                <input type="checkbox" [(ngModel)]="newGrantAreas[a]" /> {{ areaLabels[a] || a }}
              </label>
            </div>
            <button *ngIf="newGrantUser" mat-flat-button color="primary" (click)="addGrant()">Give access</button>
          </div>
        </div>

        <div class="set-section">
          <div class="set-title">Phone links</div>
          <div class="set-help">Links texted with "Send to my phone". Turn one off if a phone is lost.</div>
          <div *ngFor="let l of phoneLinks" class="set-row" [class.off]="l.revoked">
            <div class="set-name">{{ l.user }} <span class="set-muted">{{ l.sent_to }}</span></div>
            <div class="set-muted">
              Sent {{ l.created_at | safeDate:'MMM d' }} ·
              {{ l.revoked ? 'turned off' : (l.device_bound ? 'in use on a phone' : 'not opened yet') }}
              <span *ngIf="l.last_used_at"> · last used {{ l.last_used_at | safeDate:'MMM d, h:mm a' }}</span>
            </div>
            <button *ngIf="!l.revoked" class="set-link danger" (click)="revokeLink(l)">Turn off</button>
          </div>
          <div *ngIf="!phoneLinks.length" class="set-help">No links sent yet.</div>
        </div>
      </ng-container>
    </div>

    <div *ngIf="showActivity && !needsPin" class="chat-messages activity">
      <div *ngIf="activityLoading" class="typing-indicator"><span class="dot"></span><span class="dot"></span><span class="dot"></span></div>
      <div *ngIf="activityError" class="activity-error">{{ activityError }}</div>
      <div *ngIf="!activityLoading && !activityError && !activity.length" class="activity-empty">No changes yet.</div>
      <div *ngFor="let row of activity" class="activity-row" [ngClass]="'status-' + row.status">
        <div class="activity-top">
          <span class="activity-tool">{{ toolLabel(row.tool) }}</span>
          <span class="activity-status">{{ statusLabel(row.status) }}</span>
        </div>
        <div class="activity-meta">{{ row.created_at | safeDate:'MMM d, h:mm a' }} · {{ row.user || 'unknown' }} · {{ row.channel }}</div>
        <div class="activity-msg" *ngIf="row.message">“{{ row.message }}”</div>
      </div>
    </div>

    <div *ngIf="!showActivity && !showSettings && !needsPin" class="chat-messages" #messageContainer>
      <div *ngFor="let msg of messages" class="chat-bubble"
           [ngClass]="{'user-bubble': msg.role === 'user', 'assistant-bubble': msg.role === 'assistant', 'error-bubble': msg.error, 'has-card': shownActions(msg).length || msg.pending?.length}">
        <div class="bubble-text" [innerHTML]="formatMessage(msg.text)"></div>

        <app-agent-result *ngFor="let a of shownActions(msg)" [action]="a" (undo)="undo($event)"></app-agent-result>

        <div *ngFor="let card of msg.pending" class="confirm-card" [ngClass]="'state-' + card.state">
          <div class="confirm-title">
            <mat-icon>warning_amber</mat-icon> Needs your confirmation
          </div>
          <ng-container *ngIf="card.summary as s; else rawPreview">
            <div class="confirm-what"><b>{{ s.title }}</b><span *ngIf="s.target">: {{ s.target }}</span></div>
            <table *ngIf="s.changes?.length" class="confirm-changes">
              <tr *ngFor="let c of s.changes">
                <td class="cc-field">{{ c.field }}</td>
                <td class="cc-from">{{ c.from ?? '' }}</td>
                <td class="cc-arrow" *ngIf="c.from !== null && c.from !== undefined">→</td>
                <td class="cc-to" [attr.colspan]="c.from === null || c.from === undefined ? 2 : 1"><b>{{ c.to }}</b></td>
              </tr>
            </table>
            <div *ngIf="!s.changes?.length && s.note" class="confirm-preview">{{ s.note }}</div>
          </ng-container>
          <ng-template #rawPreview><div class="confirm-preview">{{ previewText(card) }}</div></ng-template>
          <div *ngIf="card.state === 'waiting' || card.state === 'running'" class="confirm-actions">
            <button mat-flat-button color="primary" [disabled]="card.state === 'running'" (click)="confirm(card)">
              {{ card.state === 'running' ? 'Working…' : 'Confirm' }}
            </button>
            <button mat-stroked-button [disabled]="card.state === 'running'" (click)="cancel(card)">Cancel</button>
          </div>
          <div *ngIf="card.state === 'waiting' && card.undo_note" class="confirm-note confirm-warn">{{ card.undo_note }}</div>
          <div *ngIf="card.note" class="confirm-note">{{ card.note }}</div>
          <div *ngIf="canUndoCard(card) || card.state === 'undoing'" class="confirm-actions">
            <button mat-stroked-button [disabled]="card.state === 'undoing'" (click)="undoCard(card)">
              <mat-icon>undo</mat-icon> {{ card.state === 'undoing' ? 'Undoing…' : 'Undo' }}
            </button>
          </div>
        </div>
      </div>

      <div *ngIf="messages.length === 1 && messages[0].role === 'assistant'" class="suggestions">
        <button *ngFor="let s of suggestions" mat-stroked-button class="suggestion-btn" (click)="sendMessage(s)">
          {{ s }}
        </button>
      </div>

      <div *ngIf="isLoading" class="typing-indicator">
        <span class="dot"></span><span class="dot"></span><span class="dot"></span>
        <span *ngIf="waitText" class="wait-text">{{ waitText }}</span>
      </div>
    </div>

    <div *ngIf="!showActivity && !showSettings && !needsPin" class="chat-input-area">
      <input type="text" class="chat-input" placeholder="Ask or tell me what to change…" maxlength="2000"
             [(ngModel)]="userInput" (keydown)="onKeyDown($event)" [disabled]="isLoading" />
      <button mat-icon-button class="send-btn" [disabled]="!userInput?.trim() || isLoading" (click)="sendMessage()">
        <mat-icon>send</mat-icon>
      </button>
    </div>
  </div>
</div>
